import {
  Button,
  Dialog,
  Dropdown,
  InputText,
  InputTextarea,
  Typography,
} from '@sl/react-datafabric-components';
import { FC, useEffect, useMemo, useState } from 'react';

type CancelSubscriptionDialogProps = {
  visible: boolean;
  planName: string;
  loading?: boolean;
  onHide: () => void;
  onConfirm: (values: { reason: string; comment?: string }) => void;
};

const cancellationReasons = [
  { label: 'Too expensive for now', value: 'too_expensive' },
  { label: 'Missing features I need', value: 'missing_features' },
  { label: 'Not using it enough', value: 'low_usage' },
  { label: 'Switching to another solution', value: 'switching_solution' },
  { label: 'Temporary pause', value: 'temporary_pause' },
  { label: 'Other', value: 'other' },
];

export const CancelSubscriptionDialog: FC<CancelSubscriptionDialogProps> = ({
  visible,
  planName,
  loading = false,
  onHide,
  onConfirm,
}) => {
  const [reason, setReason] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [planConfirmation, setPlanConfirmation] = useState<string>('');

  const normalizedPlanName = useMemo(
    () => String(planName || '').trim().toLowerCase(),
    [planName],
  );

  useEffect(() => {
    if (!visible) return;
    setReason('');
    setComment('');
    setPlanConfirmation('');
  }, [visible]);

  const isPlanConfirmed =
    !normalizedPlanName ||
    planConfirmation.trim().toLowerCase() === normalizedPlanName;
  const canSubmit = Boolean(reason) && isPlanConfirmed && !loading;

  return (
    <Dialog
      header='Cancel auto-renewal'
      visible={visible}
      onHide={onHide}
      style={{ width: '560px', maxWidth: '95vw' }}
      closable={!loading}
    >
      <div className='flex flex-column gap-3'>
        <Typography className='m-0'>
          Your subscription will stay active until the end of the current
          billing cycle, then it will not renew automatically.
        </Typography>

        <div className='flex flex-column gap-2'>
          <Typography className='font-medium'>Reason for cancellation</Typography>
          <Dropdown
            value={reason}
            options={cancellationReasons}
            optionLabel='label'
            optionValue='value'
            placeholder='Select a reason'
            onChange={(event: any) => setReason(String(event?.value || ''))}
            className='w-full'
          />
        </div>

        <div className='flex flex-column gap-2'>
          <Typography className='font-medium'>
            Additional comments (optional)
          </Typography>
          <InputTextarea
            value={comment}
            rows={4}
            autoResize
            placeholder='Tell us what would have helped you stay subscribed'
            onChange={(event: any) =>
              setComment(String(event?.target?.value || ''))
            }
          />
        </div>

        <div className='flex flex-column gap-2'>
          <Typography className='font-medium'>
            Confirm by typing plan name: <strong>{planName}</strong>
          </Typography>
          <InputText
            value={planConfirmation}
            placeholder='Type the plan name to confirm'
            onChange={(event: any) =>
              setPlanConfirmation(String(event?.target?.value || ''))
            }
          />
          {!isPlanConfirmed && (
            <Typography className='text-red-500 text-sm'>
              The plan name does not match.
            </Typography>
          )}
        </div>

        <div className='flex justify-content-end gap-2 mt-2'>
          <Button
            label='Keep subscription'
            severity='secondary'
            outlined
            onClick={onHide}
            disabled={loading}
          />
          <Button
            label='Cancel auto-renewal'
            icon='pi pi-ban'
            severity='danger'
            loading={loading}
            disabled={!canSubmit}
            onClick={() => {
              const normalizedComment = comment.trim();
              onConfirm({
                reason,
                ...(normalizedComment ? { comment: normalizedComment } : {}),
              });
            }}
          />
        </div>
      </div>
    </Dialog>
  );
};

